import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map, tap } from 'rxjs';

import { environment } from '@environments/environment.dev';
import { HttpService } from '@services/http.service';
import {
    IReqCreditRequest,
    IReqWalletPage,
    IResAiUsage,
    IResCreateCreditRequest,
    IResCreditRequests,
    IResCreditTransactions,
    IResWalletRaw,
    IWalletSummary,
} from '@shared/model/wallet.model';

/**
 * Tenant user's credit wallet (backend: /api/token-*). The user is always taken from the JWT
 * on the backend; nothing here addresses another user. The latest summary is shared through
 * `wallet$` and refreshed on events (page load, credit request, AI call) — never polled.
 */
@Injectable({
    providedIn: 'root',
})
export class WalletService {
    public API_URL = `${environment.apiEndpoint}`;

    private readonly walletSubject = new BehaviorSubject<IWalletSummary | null>(null);
    public readonly wallet$ = this.walletSubject.asObservable();

    constructor(private readonly httpService: HttpService) {}

    getWallet(): Observable<IWalletSummary> {
        return this.httpService.get<IResWalletRaw>(`${this.API_URL}token-wallet`).pipe(
            map((response) => ({
                available_tokens: Number(response.data.available_tokens),
                reserved_tokens: Number(response.data.reserved_tokens),
                lifetime_credited: Number(response.data.lifetime_credited),
                lifetime_used: Number(response.data.lifetime_used),
                warning_threshold_percent: Number(response.data.warning_threshold_percent),
                status: response.data.status,
            })),
            tap((wallet) => this.walletSubject.next(wallet))
        );
    }

    /** Fire-and-forget refresh of the shared summary; failures leave the last known value. */
    refreshWallet(): void {
        this.getWallet().subscribe({ error: () => undefined });
    }

    clearWallet(): void {
        this.walletSubject.next(null);
    }

    getUsage(page: IReqWalletPage): Observable<IResAiUsage> {
        return this.httpService.get<IResAiUsage>(`${this.API_URL}token-usage`, {
            params: this.pageParams(page),
        });
    }

    getTransactions(page: IReqWalletPage): Observable<IResCreditTransactions> {
        return this.httpService.get<IResCreditTransactions>(`${this.API_URL}token-transactions`, {
            params: this.pageParams(page),
        });
    }

    getCreditRequests(page: IReqWalletPage): Observable<IResCreditRequests> {
        return this.httpService.get<IResCreditRequests>(`${this.API_URL}token-requests`, {
            params: this.pageParams(page),
        });
    }

    createCreditRequest(body: IReqCreditRequest): Observable<IResCreateCreditRequest> {
        return this.httpService
            .post<IResCreateCreditRequest>(`${this.API_URL}token-requests`, body)
            .pipe(tap(() => this.refreshWallet()));
    }

    private pageParams(page: IReqWalletPage): { pageNo: string; pageSize: string } {
        return { pageNo: page.pageNo.toString(), pageSize: page.pageSize.toString() };
    }
}
